import { HikvisionCameraDirectionEnum } from '@zaoblako/shared/modules/cctv/enums/hikvision-camera-direction.enum';
import { HikvisionCameraDirectionEnumI18n } from '@zaoblako/shared/modules/cctv/i18n/hikvision-camera-direction-enum.i18n';
import { BarrierOpenModeEnum } from '@zaoblako/shared/modules/cctv/enums/barrier-open-mode.enum';
import { BarrierOpenModeEnumI18n } from '@zaoblako/shared/modules/cctv/i18n/barrier-open-mode-enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CctvDtoKeysEnum } from '@zaoblako/shared/modules/cctv/enums/cctv-dto-keys.enum';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { idLikeSchema } from '@zaoblako/shared/modules/serde/schemas/id-like.schema';
import { entityLikeSchemaFactory } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import type { ConverterFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-find.dto';
import type { NetPingDeviceFindInterface } from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-find.dto';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';

import * as yup from 'yup';

/** "Hikvision" CCTV camera credentials create and update APIs request body - DTO signature */
export interface HikvisionCameraFormInterface {
  /** Camera display name */
  name: string;
  /** Camera direction */
  direction: HikvisionCameraDirectionEnum;
  /** If `true`, then secured connection will be used */
  secured?: boolean;
  /** If `true`, then barrier will not be opened (events still saved) */
  doNotOpenBarrier?: boolean;
  /** Camera connection host (IP-address or domain name) */
  host: string;
  /** Camera connection port number */
  port: number;
  /** Camera connection login */
  login: string;
  /** Camera connection login */
  password: string;
  /** Barrier opening mode ({@link BarrierOpenModeEnum.ISAPI} by default) */
  barrierOpenMode?: BarrierOpenModeEnum;
  /** Converter connected to this camera */
  converter?: ConverterFindInterface;
  /** "NetPing" device converted to this camera */
  netPingDevice?: NetPingDeviceFindInterface;
}

/** Validation context. For internal usage only. */
type HikvisionCameraFormValidationContext = SerdeContextInterface<SerdeContextDataInterface, HikvisionCameraFormInterface>;

/** "Hikvision" CCTV camera credentials create and update APIs request body - DTO schema */
export const HikvisionCameraFormDto = SerdeDtoClass.create<HikvisionCameraFormInterface>(CctvDtoKeysEnum.HIKVISION_CAMERA_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: nonEmptyStringSchema,
  },
  direction: {
    label: 'Направление',
    serializer: commonEnumValidator(HikvisionCameraDirectionEnum, HikvisionCameraDirectionEnumI18n),
  },
  secured: {
    optional: true,
    label: 'Использовать защищенное соединение',
    serializer: yup.boolean(),
  },
  doNotOpenBarrier: {
    optional: true,
    label: 'Не открывать шлагбаум',
    serializer: yup.boolean(),
  },
  host: {
    label: 'Хост (IP-адрес или доменное имя)',
    serializer: nonEmptyStringSchema,
  },
  port: {
    label: 'Порт подключения',
    serializer: idLikeSchema,
  },
  login: {
    serializer: nonEmptyStringSchema,
    label: 'Логин',
  },
  password: {
    serializer: nonEmptyStringSchema,
    label: 'Пароль',
  },
  barrierOpenMode: {
    optional: true,
    label: 'Как открывать шлагбаум',
    serializer: commonEnumValidator(BarrierOpenModeEnum, BarrierOpenModeEnumI18n),
  },
  converter: {
    optional: (context: HikvisionCameraFormValidationContext) => context?.value?.barrierOpenMode !== BarrierOpenModeEnum.CONVERTER,
    label: 'СКУД-контроллер',
    serializer: entityLikeSchemaFactory(
      (context: HikvisionCameraFormValidationContext) => context?.value?.barrierOpenMode === BarrierOpenModeEnum.CONVERTER,
    ),
  },
  netPingDevice: {
    optional: (context: HikvisionCameraFormValidationContext) => context?.value?.barrierOpenMode !== BarrierOpenModeEnum.NET_PING_DEVICE,
    label: 'Устройство "NetPing"',
    serializer: entityLikeSchemaFactory(
      (context: HikvisionCameraFormValidationContext) => context?.value?.barrierOpenMode === BarrierOpenModeEnum.NET_PING_DEVICE,
    ),
  },
});
